<script lang="ts">
  // The key guide: the keys live on the page, generated from the bindings
  // table by family (keys/guide.ts). One instance, in App.
  import { keyboard } from '../keys/keyboard.svelte';

  let dialog: HTMLDialogElement | undefined = $state();
  const view = $derived(keyboard.guideOpen ? keyboard.guide : null);
  $effect(() => {
    if (!dialog) return;
    if (view && !dialog.open) dialog.showModal();
    else if (!view && dialog.open) dialog.close();
  });

  // While open, the guide is a modal scope: its key closes it and nothing under it sees a key.
  // Keyed on the open flag alone: registering changes the scopes, which `view` reads.
  const open = $derived(!!view);
  $effect(() => {
    if (!open) return;
    return keyboard.use({ scope: 'guide', handle: (c) => (c.kind === 'cancel' ? keyboard.closeGuide() : 'pass') });
  });
  const closeKey = $derived(open ? keyboard.key({ kind: 'cancel' }) : null);

  /** A key as the guide draws it: the key itself, and (for a prefix that takes an object letter) what follows it. */
  const SLOT = ' + object';
  const keyParts = (k: string) => (k.endsWith(SLOT) ? { key: k.slice(0, -SLOT.length), slot: 'object' } : { key: k, slot: null });

  /** Prose with `backticks` around keys, as text and key parts. */
  const parts = (text: string) => text.split('`').map((t, i) => ({ t, key: i % 2 === 1 }));
</script>

<dialog
  class="sheet keyhelp"
  bind:this={dialog}
  onclose={() => keyboard.closeGuide()}
  onclick={(e) => {
    if (e.target === dialog) keyboard.closeGuide();
  }}
  aria-labelledby="keyhelp-title"
>
  {#if view}
    <div class="sheet-head">
      <h2 id="keyhelp-title">{view.heading}</h2>
      <button class="btn small" onclick={() => keyboard.closeGuide()}>Close{closeKey ? ` · ${closeKey}` : ''}</button>
    </div>
    <p>{#each parts(view.intro) as p}{#if p.key}<kbd>{p.t}</kbd>{:else}{p.t}{/if}{/each}</p>
    {#each view.sections as g (g.id)}
      <h3 id="keys-{g.id}">{g.title}</h3>
      <dl aria-labelledby="keys-{g.id}">
        {#each g.rows as row}
          <dt>
            {#each row.keys as k, i}{@const p = keyParts(k)}<span class="keys">{#if i}&nbsp;/&nbsp;{/if}<kbd>{p.key}</kbd>{#if p.slot}<span class="slot">&nbsp;+&nbsp;<i>{p.slot}</i></span>{/if}</span>{/each}
          </dt>
          <dd>{#each parts(row.does) as p}{#if p.key}<kbd>{p.t}</kbd>{:else}{p.t}{/if}{/each}{#if row.note}{' '}<span class="muted">({row.note})</span>{/if}</dd>
        {/each}
      </dl>
    {/each}
    {#each view.sections as g (g.id)}
      {#if g.notes}<p>{#each parts(g.notes) as p}{#if p.key}<kbd>{p.t}</kbd>{:else}{p.t}{/if}{/each}</p>{/if}
    {/each}
  {/if}
</dialog>
